import React from "react";
import { Box, Container, Typography, Grid, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Code2, ClipboardList, Star, GraduationCap, Heart, ShoppingBag } from "lucide-react";
import BRAND from "../theme/talktokam";

const platforms = [
  {
    name: "Discord",
    icon: MessageCircle,
    color: "#6366f1",
    desc: "Real-time chat with fellow Talktokam builders. Get help, share tips, and showcase your bots.",
  },
  {
    name: "GitHub Discussions",
    icon: Code2,
    color: "#10b981",
    desc: "Feature requests, bug reports, and technical discussions with the engineering team.",
  },
  {
    name: "Community Forum",
    icon: ClipboardList,
    color: "#f59e0b",
    desc: "Browse community-contributed templates, best practices, and success stories.",
  },
  {
    name: "Ambassador Program",
    icon: Star,
    color: "#f472b6",
    desc: "Join our ambassador program — earn rewards for helping others and spreading the word.",
  },
];

const Community = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ background: "#050816", minHeight: "100vh" }}>
      <Box
        sx={{
          position: "relative",
          textAlign: "center",
          py: { xs: 10, md: 14 },
          background: `radial-gradient(ellipse 800px 600px at 20% 10%, ${BRAND.cyan}33 0%, transparent 60%), radial-gradient(ellipse 700px 500px at 85% 90%, ${BRAND.purple}33 0%, transparent 60%), radial-gradient(ellipse 600px 400px at 50% 50%, ${BRAND.primary}1a 0%, transparent 60%), linear-gradient(180deg, #050816 0%, #0a0e27 50%, #050816 100%)`,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage: `linear-gradient(rgba(56,189,248,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(56,189,248,0.04) 1px, transparent 1px)`,
            backgroundSize: "60px 60px",
            maskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            WebkitMaskImage:
              "radial-gradient(ellipse at center, black 30%, transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -40, 0], scale: [1, 1.1, 1] }}
          transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "5%",
            left: "-5%",
            width: 500,
            height: 500,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.cyan}40 0%, transparent 70%)`,
            filter: "blur(60px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, 40, 0], scale: [1, 1.15, 1] }}
          transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            bottom: "-10%",
            right: "-10%",
            width: 600,
            height: 600,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.purple}40 0%, transparent 70%)`,
            filter: "blur(80px)",
            pointerEvents: "none",
          }}
        />
        <motion.div
          animate={{ y: [0, -25, 0], x: [0, 20, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{
            position: "absolute",
            top: "50%",
            right: "30%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${BRAND.primary}26 0%, transparent 70%)`,
            filter: "blur(40px)",
            pointerEvents: "none",
          }}
        />
        <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Typography
              variant="h2"
              sx={{
                fontWeight: 800,
                fontSize: { xs: "2rem", md: "3rem" },
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 2,
              }}
            >
              Join the Talktokam Community
            </Typography>
            <Typography
              sx={{
                fontSize: "1.1rem",
                color: "#94a3b8",
                maxWidth: 550,
                mx: "auto",
              }}
            >
              Connect with thousands of builders, share knowledge, and shape the
              future of conversational AI.
            </Typography>
          </motion.div>
        </Container>
      </Box>

      <Box
        sx={{ background: "linear-gradient(180deg, #0a0e27 0%, #050816 100%)" }}
      >
        <Container sx={{ py: { xs: 6, md: 10 } }}>
          <Grid container spacing={3}>
            {platforms.map((p, i) => (
              <Grid item xs={12} sm={6} key={i}>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                >
                  <Box
                    sx={{
                      p: 4,
                      borderRadius: 4,
                      bgcolor: "rgba(255,255,255,0.05)",
                      backdropFilter: "blur(16px)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      textAlign: "center",
                      height: "100%",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: "rgba(56,189,248,0.3)",
                        boxShadow: "0 12px 40px rgba(56,189,248,0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 64,
                        height: 64,
                        borderRadius: "20px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 1.5,
                        background: `linear-gradient(135deg, ${p.color}33, ${p.color}11)`,
                        border: `1px solid ${p.color}44`,
                        boxShadow: `0 0 20px ${p.color}22`,
                      }}
                    >
                      <p.icon size={28} color={p.color} />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: "1.2rem",
                        color: "#fff",
                        mb: 1,
                      }}
                    >
                      {p.name}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "0.9rem",
                        color: "#94a3b8",
                        mb: 2,
                        lineHeight: 1.6,
                      }}
                    >
                      {p.desc}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        borderColor: "rgba(99,102,241,0.5)",
                        color: "#6366f1",
                        "&:hover": {
                          borderColor: "#6366f1",
                          bgcolor: "rgba(99,102,241,0.1)",
                        },
                      }}
                    >
                      Join Now
                    </Button>
                  </Box>
                </motion.div>
              </Grid>
            ))}
          </Grid>

          <Box
            sx={{
              mt: 6,
              p: 4,
              borderRadius: 4,
              bgcolor: "rgba(255,255,255,0.05)",
              backdropFilter: "blur(16px)",
              border: "1px solid rgba(255,255,255,0.08)",
              textAlign: "center",
              transition: "all 0.3s",
              "&:hover": {
                transform: "translateY(-4px)",
                borderColor: "rgba(56,189,248,0.3)",
                boxShadow: "0 12px 40px rgba(56,189,248,0.1)",
              },
            }}
          >
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1.2rem",
                background: "linear-gradient(135deg, #38bdf8, #a855f7)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                mb: 1,
              }}
            >
              Featured Community Bots
            </Typography>
            <Typography sx={{ color: "#94a3b8", mb: 3 }}>
              Check out what the community is building with Talktokam.
            </Typography>
            <Grid container spacing={2} justifyContent="center">
              {[
                {
                  name: "EduBot",
                  desc: "Admissions counselor for a university",
                  icon: GraduationCap,
                  color: "#38bdf8",
                },
                {
                  name: "MediAssist",
                  desc: "Patient appointment scheduler",
                  icon: Heart,
                  color: "#f43f5e",
                },
                {
                  name: "ShopBot",
                  desc: "E-commerce product recommender",
                  icon: ShoppingBag,
                  color: "#f59e0b",
                },
              ].map((bot, i) => (
                <Grid item xs={12} sm={4} key={i}>
                  <Box
                    sx={{
                      p: 2,
                      borderRadius: 3,
                      bgcolor: "rgba(255,255,255,0.05)",
                      border: "1px solid rgba(255,255,255,0.08)",
                      transition: "all 0.3s",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        borderColor: "rgba(56,189,248,0.3)",
                        boxShadow: "0 12px 40px rgba(56,189,248,0.1)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: "14px",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 0.5,
                        background: `linear-gradient(135deg, ${bot.color}33, ${bot.color}11)`,
                        border: `1px solid ${bot.color}44`,
                      }}
                    >
                      <bot.icon size={20} color={bot.color} />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: "0.9rem",
                        color: "#fff",
                      }}
                    >
                      {bot.name}
                    </Typography>
                    <Typography sx={{ fontSize: "0.8rem", color: "#94a3b8" }}>
                      {bot.desc}
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>

          <Box sx={{ mt: 6, textAlign: "center" }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "1rem",
                color: "#cbd5e1",
                mb: 1,
              }}
            >
              Built something amazing?
            </Typography>
            <Typography sx={{ color: "#94a3b8", mb: 2 }}>
              Share your bot with the community.
            </Typography>
            <Button
              variant="contained"
              onClick={() => navigate("/contact")}
              sx={{
                borderRadius: "10px",
                textTransform: "none",
                fontWeight: 700,
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                boxShadow: "0 4px 15px rgba(79,70,229,0.4)",
                "&:hover": { boxShadow: "0 6px 25px rgba(79,70,229,0.6)" },
              }}
            >
              Submit Your Bot
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Community;
